// Виртуальный экран: единицы макета, размещение объектов на нём, сцена и место для неё на stage.
//
// Игра живёт в виртуальных единицах макета, а не в пикселях канваса. Макет вписан в канвас
// целиком, поэтому по одной оси видно ровно макет, а по другой — макет и поля по краям.
// Начало координат — центр макета, он же центр канваса.
//
// Сцена — это то, что игрок видит в одном состоянии игры. На экране всегда ровно одна сцена,
// её держит SceneHost: он вписывает макет в канвас и раздаёт сцене кадры и ресайз.

import { Container, type Application, type Ticker } from 'pixi.js';

import { LOGO_SKELETON } from '../assets/catalog';
import type { SkeletonData, Spine } from '../assets/manager';

// Ассеты нарисованы под экран 1920x1080 (GDD, раздел 1.3).

/** Ширина макета в виртуальных единицах. */
export const DESIGN_WIDTH: number = 1920;

/** Высота макета в виртуальных единицах. */
export const DESIGN_HEIGHT: number = 1080;

/**
 * Отступ кнопки от края макета.
 *
 * Он один на все кнопки по углам: спин, помощь и звук стоят от края одинаково, иначе угол
 * читается как перекос. Отступ берётся от макета, а не от экрана: на широком экране поля
 * по краям остаются пустыми, и кнопки не уезжают от машины.
 */
export const EDGE_MARGIN: number = 60;

/**
 * Видимая область в виртуальных единицах.
 *
 * Она не меньше макета: по одной оси совпадает с ним, по другой прибавляет поля по краям.
 */
export interface Viewport {
  readonly width: number;
  readonly height: number;
  /** Сколько физических пикселей приходится на единицу макета. */
  readonly scale: number;
  /** Какая сторона исходного прямоугольника 1920×1080 ограничила масштаб. */
  readonly fit: 'width' | 'height';
}

/**
 * Сглаживает ход: движение трогается и останавливается плавно, а не рывком.
 *
 * Кривая одна на всю игру: так ход взгляда и подъезд машины двигаются одинаково, и игрок
 * читает их как одно движение камеры, а не как два разных приёма.
 *
 * @param passed Доля пройденного пути от нуля до единицы.
 */
export function smoothStep(passed: number): number {
  return passed * passed * (3 - 2 * passed);
}

/**
 * Габарит скелета из его заголовка.
 *
 * Нулевой край Spine в файл не пишет, а рантайм ставит поле как есть, без нуля вместо
 * пропуска. Поэтому края здесь необязательные: у панели общего множителя левый край
 * стоит на нуле, и в её заголовке его нет.
 */
export interface SpineBounds {
  readonly x?: number;
  readonly y?: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Куда встаёт начало координат скелета, чтобы середина габарита пришлась в заданную точку.
 *
 * Ось Y у Spine смотрит вверх, а у Pixi вниз, поэтому по вертикали середина прибавляется,
 * а не вычитается.
 */
export function spineOrigin(bounds: SpineBounds, scale: number, x: number, y: number): { x: number; y: number } {
  return {
    x: x - ((bounds.x ?? 0) + bounds.width / 2) * scale,
    y: y + ((bounds.y ?? 0) + bounds.height / 2) * scale,
  };
}

/**
 * Ставит скелет серединой его габарита в заданную точку.
 *
 * Spine задаёт габарит от начала координат скелета, поэтому середина считается по габариту,
 * а не по позиции скелета.
 */
export function placeSpine(spine: Spine, scale: number, x: number, y: number): void {
  const data: SkeletonData = spine.skeleton.data;
  const origin = spineOrigin(data, scale, x, y);

  spine.scale.set(scale);
  spine.position.set(origin.x, origin.y);
}

/**
 * Длина анимации скелета в секундах.
 *
 * По ней ведётся ожидание конца анимации: сцена копит время кадрами и сравнивает его с этой
 * длиной. Незнакомая анимация длится нисколько, поэтому ожидание на ней не виснет.
 */
export function animationSeconds(spine: Spine, animation: string): number {
  return spine.skeleton.data.findAnimation(animation)?.duration ?? 0;
}

/**
 * Ставит скелет в последний кадр анимации и держит его там.
 *
 * Так задаются крайние состояния: закрытая створка — это конец анимации закрытия, открытая —
 * конец открытия. Отдельных поз под них в скелетах нет.
 */
export function holdSpine(spine: Spine, animation: string): void {
  const track = spine.state.setAnimation(0, animation, false);
  track.trackTime = track.animationEnd;
}

/**
 * Ставит скелет в первый кадр анимации и держит его там.
 *
 * Так задаются позы, с которых анимация начинается: значение множителя стоит на символе
 * с первого кадра win_1…win_10, и отдельной позы под него в скелете нет.
 *
 * Время у дорожки остановлено, поэтому анимация не идёт дальше первого кадра.
 */
export function poseSpine(spine: Spine, animation: string): void {
  const track = spine.state.setAnimation(0, animation, false);
  track.trackTime = 0;
  track.timeScale = 0;
}

/**
 * Готовит логотип игры: ставит по центру макета и пускает анимацию с дымом.
 *
 * @param height Высота середины логотипа в координатах макета.
 */
export function showLogo(logo: Spine, scale: number, height: number): void {
  placeSpine(logo, scale, 0, height);
  logo.state.setAnimation(0, LOGO_SKELETON.animations.smoke, true);
}

/** Экран игры. */
export interface Scene {
  /** Узел сцены. Хост держит его на виртуальном экране, пока сцена показана. */
  readonly view: Container;

  /** Раскладывает содержимое под текущую видимую область. */
  layout(viewport: Viewport): void;

  /** Освобождает узлы сцены. Текстуры остаются в кэше Pixi. */
  destroy(): void;

  /** Двигает содержимое сцены. Хост зовёт каждый кадр и передаёт время с прошлого кадра в секундах. */
  update?(seconds: number): void;
}

/** Виртуальный экран на stage: на нём всегда ровно одна сцена. */
export class SceneHost {
  readonly #app: Application;
  readonly #root: Container = new Container();
  #scene: Scene | null = null;

  constructor(app: Application) {
    this.#app = app;
    app.stage.addChild(this.#root);
    app.renderer.on('resize', this.#layout);
    app.ticker.add(this.#tick);
  }

  /** Ставит сцену вместо текущей. Прежняя сцена уничтожается. */
  show(scene: Scene): void {
    this.#clear();

    this.#scene = scene;
    this.#root.addChild(scene.view);
    this.#layout();
  }

  /** Убирает сцену и отписывается от кадров и ресайза. */
  destroy(): void {
    this.#app.renderer.off('resize', this.#layout);
    this.#app.ticker.remove(this.#tick);
    this.#clear();
    this.#app.stage.removeChild(this.#root);
  }

  // Ticker считает время в миллисекундах, а движение сцены — в секундах: так скорости
  // и выдержки в игре записаны в тех же единицах, что и в правилах.
  readonly #tick = (ticker: Ticker): void => {
    this.#scene?.update?.(ticker.deltaMS / 1000);
  };

  #clear(): void {
    if (this.#scene === null)
      return;

    this.#root.removeChild(this.#scene.view);
    this.#scene.destroy();
    this.#scene = null;
  }

  readonly #layout = (): void => {
    const { width, height } = this.#app.screen;

    // Макет вписан в канвас целиком, поэтому масштаб один на обе оси, а начало координат
    // стоит в центре канваса. Что не занял макет, остаётся полями по краям.
    const fit: 'width' | 'height' = width / DESIGN_WIDTH <= height / DESIGN_HEIGHT ? 'width' : 'height';
    const scale: number = fit === 'width' ? width / DESIGN_WIDTH : height / DESIGN_HEIGHT;
    this.#root.scale.set(scale);
    this.#root.position.set(width / 2, height / 2);

    this.#scene?.layout({ width: width / scale, height: height / scale, scale, fit });
  };
}
